import { useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import appSettings from '@/config/appSettings'
import RequireRole from '@/components/auth/RequireRole'
import { ENVIRONMENT } from '@/config/constants'
import ControlCenter from '@/pages/ControlCenter'
import Configuration from '@/pages/ControlCenter/Configuration'
import Home from '@/pages/Home'
import Loading from '@/pages/Loading'
import Login from '@/pages/Login'
import NotFound from '@/pages/NotFound'
import { setDatadogUser } from '@/utils/datadog'
import { getAvatar, getUser } from '@/utils/getUser'
import { useMsal } from '@azure/msal-react'

import { useAuthStore } from './stores/auth.store'

function App() {
  const { accounts, inProgress } = useMsal()

  const setUser = useAuthStore((store) => store.setUser)

  const userLoading = useAuthStore((store) => store.userLoading)
  const setUserLoading = useAuthStore((store) => store.setUserLoading)

  useEffect(() => {
    // Fetch user info after login
    if (accounts && accounts.length > 0) {
      setUserLoading(true)
      getUser().then(async (user) => {
        if (user) {
          const initials = (
            (user.givenName?.[0] || '') + (user.surname?.[0] || '')
          ).toUpperCase()

          let avatarUrl: string | undefined
          try {
            const avatarBlob = await getAvatar(user.id)
            if (avatarBlob) {
              avatarUrl = URL.createObjectURL(avatarBlob)
            }
          } catch (e) {
            console.log('Error fetching avatar:', e)
            avatarUrl = undefined
          }
          setUser({
            ...user,
            initials,
            avatarUrl,
          })

          // Set Datadog user context after authentication
          setDatadogUser({
            id: user.id || user.userPrincipalName,
            name: user.displayName || undefined,
            email: user.mail || user.userPrincipalName,
          })
        }
        setUserLoading(false)
      })
    } else {
      setUser(null)
      setUserLoading(false)
    }
  }, [accounts, setUser, setUserLoading])

  useEffect(() => {
    if (ENVIRONMENT === 'development') {
      document.title = `[DEV] ${appSettings.name}`
    }
  }, [])

  if (inProgress !== 'none' || userLoading) {
    return <Loading />
  }

  // If no accounts, show login page
  if (!accounts || accounts.length === 0) {
    return <Login />
  }

  // If authenticated, show routes
  return (
    <Routes>
      <Route path='/' element={<Home />} />
      <Route
        path='/control-center'
        element={<Navigate to='/control-center/alerts' replace />}
      />
      <Route
        path='/control-center/alerts'
        element={
          <RequireRole roles={['SUPER_ADMIN', 'ADMIN']} redirectTo='/'>
            <ControlCenter />
          </RequireRole>
        }
      />
      <Route
        path='/control-center/alerts'
        element={
          <RequireRole roles={['SUPER_ADMIN', 'ADMIN']} redirectTo='/'>
            <ControlCenter />
          </RequireRole>
        }
      />
      <Route
        path='/control-center/feature-flags'
        element={
          <RequireRole
            roles={['SUPER_ADMIN']}
            redirectTo='/control-center/alerts'
          >
            <ControlCenter />
          </RequireRole>
        }
      />

      <Route
        path='/control-center/configuration'
        element={
          <RequireRole
            roles={['SUPER_ADMIN']}
            redirectTo='/control-center/alerts'
          >
            <Configuration />
          </RequireRole>
        }
      />
      <Route path='*' element={<NotFound />} />
    </Routes>
  )
}

export default App
